import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  SITE_STATUS, PAYMENT_STATUS, DELAY_REASONS,
} from "@/lib/format";

let WORKFLOW = { stages: [] };

export function setWorkflow(wf) {
  WORKFLOW = wf && wf.stages ? wf : { stages: [] };
}

const KIND_TYPE = { date: "date", text: "text", number: "number", tick: "check", dropdown: "select" };

function fromWorkflow(field) {
  return {
    key: field.key,
    label: field.label || field.key,
    type: KIND_TYPE[field.kind] || "text",
    options: field.options || [],
    required: !!field.required,
  };
}

// the fields of a stage, read from the stages held in React state, so a screen renders
// again when the customer's flow lands
export function fieldsOf(stages, stage) {
  const s = (stages || []).find((x) => Number(x.id) === Number(stage));
  return (s?.fields || []).map(fromWorkflow);
}

/** A stage's fields paired with their read-out type, for the stage-by-stage record. */
export function readFieldsOf(stages, stage) {
  return fieldsOf(stages, stage).map((f) => ({
    key: f.key,
    label: f.label,
    type: f.type === "date" ? "date" : f.type === "number" ? "number" : null,
  }));
}

export function fieldDefIn(stages, key) {
  for (const s of stages || []) {
    const hit = (s.fields || []).find((f) => f.key === key);
    if (hit) return fromWorkflow(hit);
  }
  const legacy = F[key];
  return legacy ? { key, ...legacy, options: legacy.options || [] } : null;
}

export function fieldDef(key) {
  return fieldDefIn(WORKFLOW.stages, key);
}

export const F = {
  soNumber: { label: "SO number", type: "text" },
  poProcessedDate: { label: "PO processed date", type: "date" },
  siteStatus: { label: "Site status", type: "select", options: SITE_STATUS },
  siteReadyDate: { label: "Site ready date", type: "date" },
  expectedMaterialReadyDate: { label: "Expected material readiness date", type: "date" },
  bomSentToFactory: { label: "BOM sent to the factory", type: "check" },
  ldExemptionDone: { label: "LD exemption mail sent", type: "check" },
  dispatchInvoiceNumber: { label: "Invoice number", type: "text" },
  dispatchInvoiceDate: { label: "Invoice date", type: "date" },
  dispatchDate: { label: "Dispatch date", type: "date" },
  lrNumber: { label: "LR number", type: "text" },
  deliveryDate: { label: "Delivery date", type: "date" },
  installationDate: { label: "Installation date", type: "date" },
  vendorName: { label: "Vendor name", type: "text" },
  vendorCost: { label: "Installation charges", type: "number" },
  vendorInvoiceNumber: { label: "Vendor invoice number", type: "text" },
  vendorInvoiceDate: { label: "Vendor invoice date", type: "date" },
  billSubmissionDate: { label: "Bill submission date", type: "date" },
  grnDate: { label: "GRN date", type: "date" },
  paymentStatus: { label: "Payment status", type: "select", options: PAYMENT_STATUS },
  portalRejectionReason: { label: "Portal rejection reason", type: "textarea" },
  portalRejectionClearedDate: { label: "Rejection cleared on", type: "date" },
  signOffMailSent: { label: "Sign-off sent to the branch — this closes the line", type: "check" },
  kanexRemark: { label: "Remark from Kanex (goes into the sign-off mail)", type: "textarea" },
  delayReason: { label: "Delay reason", type: "select", options: DELAY_REASONS },
  nextFollowUpDate: { label: "Next follow-up", type: "date" },
  followUpCategory: { label: "Follow-up category", type: "text" },
  remark: { label: "Remark", type: "textarea" },
};

// the step fields for each stage
export const STEP_FIELDS = {
  2: ["siteStatus", "siteReadyDate", "expectedMaterialReadyDate", "bomSentToFactory", "ldExemptionDone"],
  6: ["dispatchInvoiceNumber", "dispatchInvoiceDate", "dispatchDate", "lrNumber"],
  7: ["deliveryDate"],
  8: ["installationDate", "vendorName", "vendorCost", "vendorInvoiceNumber", "vendorInvoiceDate"],
  10: ["billSubmissionDate"],
  11: ["grnDate", "paymentStatus", "portalRejectionReason", "portalRejectionClearedDate", "kanexRemark", "signOffMailSent"],
  13: [],
};

export function Field({ name, def, value, onChange, prefix = "step" }) {
  const cfg = def || fieldDef(name) || F[name];
  if (!cfg) return null;
  const id = `${prefix}-${name}`;
  if (cfg.type === "check") {
    return (
      <label className="flex items-start gap-2 pt-5 text-xs" htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          className="mt-0.5"
          checked={!!value}
          onChange={(e) => onChange(name, e.target.checked)}
          data-testid={`${id}-input`}
        />
        {cfg.label}
      </label>
    );
  }
  const common = {
    id,
    "data-testid": `${id}-input`,
    value: value ?? "",
    onChange: (e) => onChange(name, e.target.value),
  };
  return (
    <div>
      <Label htmlFor={id} className="field-label">{cfg.label}</Label>
      {cfg.type === "select" ? (
        <select {...common} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
          <option value="">Not recorded</option>
          {cfg.options.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      ) : cfg.type === "textarea" ? (
        <Textarea {...common} rows={2} />
      ) : (
        <Input
          {...common}
          type={cfg.type}
          className={cfg.type === "date" || cfg.type === "number" ? "font-mono tabular-nums" : ""}
        />
      )}
    </div>
  );
}

export function payloadFrom(values) {
  const payload = {};
  Object.entries(values).forEach(([k, v]) => {
    const kind = fieldDef(k)?.type || F[k]?.type;
    payload[k] = v === "" ? null : kind === "number" ? Number(v) : v;
  });
  return payload;
}
